import {
    Box,
    Divider,
    FormControl,
    FormHelperText,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";
import { useTheme } from "@mui/system";
import React, { useState } from "react";
import TreffasButton from "src/components/TreffasButton";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import http from "src/utils/http";

import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller as Fc } from "react-hook-form";
import TreffasStyle from "src/utils/TreffasStyle";

import Pendo from "src/utils/Pendo";
import { useTranslation } from "react-i18next";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useSnackbarContext } from "src/context/SnackbarContext";

const DefaultLoginForm = ({ setFormtype }) => {
    const themes = useTheme();
    const navigate = useNavigate();
    const [isProcess, setIsProcess] = useState(false);
    const [isVerifyProcess, setIsVerifyProcess] = useState(false);

    const { t } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);
    const [showLink, setShowLink] = useState({
        enable: false,
        email: null,
    });

    const { showSnackbar } = useSnackbarContext();

    const baseURL = window.location.origin;

    const Schema = Yup.object({
        // organization: Yup.string().required("Organization is a required field"),
        email: Yup.string()
            .email(t("login.form.validation.email_invalid")) //"Enter valid email address.")
            .required(t("login.form.validation.email_required")), //"Email is a required field"),
        password: Yup.string().required(
            t("login.form.validation.pasword_required")
        ), //"Password is a required field"),
    }).required();

    const {
        handleSubmit,
        control,
        setError,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(Schema),
        defaultValues: {
            // organization: "",
            email: "",
            password: "",
        },
    });

    const handleResentVerification = () => {
        let email = showLink.email;
        setIsVerifyProcess(true);
        http
            .post("/api/oauth/registration/sent-verification-email", {
                email: email,
                name: null,
                base_url: baseURL,
            })
            .then(({ data }) => {
                if (data.message === "success") {
                    showSnackbar(
                        t("registration.form.validation.verification-sent"),
                        "success"
                    );
                }
            })
            .catch((err) => {
                console.log(`error:`, err.message);
            })
            .finally(() => setIsVerifyProcess(false));
    };

    const onLogin = (values) => {
        setIsProcess(true);
        http
            .post("/api/oauth/login", {
                ...values,
                // organization: values.organization.trim().toLowerCase(),
            })
            .then(({ data }) => {
                if (data.message === "organization-not-found") {
                    setError("organization", {
                        type: "required",
                        message: t("login.form.validation.organization_not_found"), // "Organization is not found.",
                    });
                }

                if (data.message === "success") {
                    // initialize pendo //
                    Pendo.initialize(data.data);

                    if (data.two_factor_enable) {
                        navigate(
                            `/two-factor/verification?token=${data.access_token}&id=${data?.data?.id}&organization=${data.data.organization.id}&mask=${data.mask}&type=${data.two_factor_type}`
                        );
                    } else {
                        localStorage.setItem(`accessToken`, data.access_token);
                        localStorage.setItem(`__loginType`, "coach");
                        window.location.href = `/app/v2/calendar`;
                    }
                }

                if (data.message === "account-invalid") {
                    setError("submit", {
                        type: "required",
                        message: t("login.form.validation.credential_error"), //"Email or password is incorrect.",
                    });
                }
                if (data.message === "account-unverified") {
                    setShowLink({
                        enable: true,
                        email: values.email,
                    });
                    setError("submit", {
                        type: "required",
                        message: t("login.form.validation.account_unverified"), //"Account is not verified, follow the link sent in your email.",
                    });
                }
            })
            .catch((err) => {
                console.log(err.message);
            })
            .finally(() => setIsProcess(false));
    };

    return (
        <div>
            <form onSubmit={handleSubmit(onLogin)}>
                <Box sx={{ mt: "24px" }}>
                    <FormControl fullWidth variant="standard">
                        <Typography
                            sx={{
                                ...TreffasStyle.formLabel,
                                fontFamily: "var(--font-work-sans)",
                            }}
                        >
                            {t("login.form.email")}
                        </Typography>

                        <Fc
                            name="email"
                            control={control}
                            rules={{
                                required: true,
                            }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    placeholder={t("login.form.placeholder.email")}
                                    type="text"
                                    sx={TreffasStyle.formInput(themes)}
                                    error={Boolean(errors.email)}
                                    helperText={errors.email?.message}
                                    inputProps={{ "data-testid": "login_email_input" }}
                                />
                            )}
                        />
                    </FormControl>
                </Box>

                <Box sx={{ mt: "24px" }}>
                    <FormControl fullWidth variant="standard">
                        <Typography
                            sx={{
                                ...TreffasStyle.formLabel,
                                fontFamily: "var(--font-work-sans)",
                            }}
                        >
                            {t("login.form.password")}
                        </Typography>

                        <Fc
                            name="password"
                            control={control}
                            rules={{
                                required: true,
                            }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    placeholder={t("login.form.placeholder.password")}
                                    type={showPassword ? "text" : "password"}
                                    sx={TreffasStyle.formInput(themes)}
                                    error={Boolean(errors.password)}
                                    helperText={errors.password?.message}
                                    inputProps={{ "data-testid": "login_password_input" }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() =>
                                                        setShowPassword(!showPassword)
                                                    }
                                                >
                                                    {!showPassword ? (
                                                        <Visibility sx={{ fontSize: "18px" }} />
                                                    ) : (
                                                        <VisibilityOff
                                                            sx={{ fontSize: "18px" }}
                                                        />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </FormControl>

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: "8px",
                            alignItems: "center"
                        }}
                    >
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    lineHeight: "31px",
                                    textAlign: "center",
                                    color: "var(--color-brand-primary)",
                                    cursor: "pointer",
                                    fontFamily: "var(--font-grenettepro)",
                                }}
                                onClick={() => navigate("/reset/password")}
                            >
                                {t("login.form.fotgot_password")}
                            </Typography>
                        </Box>

                        <Box>
                            <Divider orientation="horizontal" sx={{ borderRight: "1px solid #0000001f", height: "18px" }} />
                        </Box>

                        <Box>
                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    lineHeight: "31px",
                                    textAlign: "center",
                                    color: "var(--color-brand-primary)",
                                    cursor: "pointer",
                                    fontFamily: "var(--font-grenettepro)",
                                }}
                                onClick={() => setFormtype("magic-link")}
                            >
                                Use magic link instead
                            </Typography>
                        </Box>

                    </Box>
                </Box>

                {errors.submit && (
                    <Box>
                        <FormHelperText
                            error
                            sx={{
                                textAlign: "center",
                            }}
                        >
                            {errors.submit.message}
                        </FormHelperText>
                    </Box>
                )}

                {showLink.enable && (
                    <Box sx={{ textAlign: "center" }}>
                        <TreffasButton
                            onClick={handleResentVerification}
                            variant="primary"
                            size="sm"
                            disabled={isVerifyProcess}
                            loading={isVerifyProcess}
                            data-testid="login_resent_verification_button"
                            sx={{ color: "#FFF" }}
                        >
                            Resent verification
                        </TreffasButton>
                    </Box>
                )}

                <Box sx={{ mt: "16px" }}>
                    <TreffasButton
                        variant="primary"
                        size="md"
                        fullWidth
                        type="submit"
                        data-testid="login_login_button"
                        sx={{
                            textTransform: "inherit",
                            borderRadius: "8px",
                            fontSize: "14px",
                        }}
                        loading={isProcess}
                        disabled={isProcess}
                    >
                        {t("login.form.login_btn")}
                    </TreffasButton>
                </Box>

                <Box sx={{ mt: "24px", pb: "24px" }}>
                    <Typography
                        sx={{
                            fontWeight: 400,
                            fontSize: "14px",
                            lineHeight: "24px",
                            textAlign: "center",
                            color: "#525F7F",
                        }}
                    >
                        {t("login.form.signup_text")}
                        <Typography
                            variant="subtitle"
                            color="primary"
                            sx={{
                                cursor: "pointer",
                                marginLeft: 0.5,
                                marginRight: 0.5,
                                color: "var(--color-brand-primary)",
                                fontFamily: "var(--font-grenettepro)",
                            }}
                            onClick={() => navigate("/register")}
                        >
                            {t("login.form.signup_btn")}
                        </Typography>
                        {t("login.form.signup_text_cont")}
                    </Typography>
                </Box>

            </form>
        </div>
    );
};

export default DefaultLoginForm;